import { useAuthContext } from '@providers/AuthProvider';

import { useWebsocketContext } from '@providers/WebsocketProvider';
import { JobResponseDto } from 'app/API';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import { useEffect } from 'react';

type TProps = {
  jobs: TPaginatedResponse<JobResponseDto>;
  setJobs: (v: TPaginatedResponse<JobResponseDto>) => void;
  organizationId: string;
  workspaceId?: string;
  reload: () => void;
};

export const WSUpdateReceiver = ({ jobs, setJobs, organizationId, workspaceId, reload }: TProps) => {
  const { organization } = useAuthContext();
  const { updatedJob, jobAccessLostId } = useWebsocketContext();

  const updateJobs = () => {
    setJobs({
      data: jobs.data.map((job) => {
        if (updatedJob?.id === job.id) {
          return updatedJob;
        }
        return job;
      }),
      count: jobs.count,
    });
  };

  useEffect(() => {
    if (!updatedJob) {
      return;
    }
    const orgWorkspaces = (organization?.workspaces ?? [])
      .filter((ws) => ws.organizationId === organizationId)
      .map((ws) => ws.id);
    if (!orgWorkspaces.includes(updatedJob.workspaceId)) {
      return;
    }
    console.log(`WSUpdateReceiver jobId: ${updatedJob?.id}`);
    const sameJobInList = jobs.data.find((job) => job.id === updatedJob.id);
    if (sameJobInList) {
      // job was moved to another workspace - need to reload
      if (workspaceId !== updatedJob.workspaceId) {
        reload();
        return;
      }
      // Job is in the list and in the same workspace - just update it without reload
      // Only update if job actually changed to avoid unnecessary re-renders
      const jobChanged =
        sameJobInList.status !== updatedJob.status ||
        sameJobInList.name !== updatedJob.name ||
        sameJobInList.deadline !== updatedJob.deadline ||
        sameJobInList.assignee?.id !== updatedJob.assignee?.id ||
        sameJobInList.duration !== updatedJob.duration ||
        sameJobInList.editingProgress !== updatedJob.editingProgress;

      if (jobChanged) {
        updateJobs();
      }
      return;
    }
    // Job is not in the list - reload if it should appear on current page
    // This happens when:
    // 1. List is empty, or
    // 2. Job was created more recently than the first job in the list (should appear at the top)
    // TODO: We can insert this job to the first position here (needs a rework on backend to send jobs only to users who have access to this job)
    const shouldAppearOnPage =
      !jobs.data?.length ||
      (updatedJob.createdAt &&
        new Date(updatedJob.createdAt).getTime() > new Date(jobs.data[0].createdAt ?? 0).getTime());

    if (shouldAppearOnPage) {
      // this is all jobs page (!workspaceId) or job is in current workspace
      if (updatedJob.workspaceId === workspaceId || !workspaceId) {
        // Always reload for new jobs that should appear on current page
        // This ensures new jobs appear immediately, regardless of when they were created
        reload();
        return;
      }
    }
  }, [updatedJob]);

  useEffect(() => {
    if (!jobAccessLostId) {
      return;
    }
    // Check if the job with lost access is in the current list
    const jobInList = jobs.data.find((job) => job.id === jobAccessLostId);
    if (jobInList) {
      // Remove the job from the list
      setJobs({
        data: jobs.data.filter((job) => job.id !== jobAccessLostId),
        count: jobs.count - 1,
      });
    }
  }, [jobAccessLostId, jobs, setJobs]);

  return null;
};
